import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import {
  fetchTaste,
  loadSavedTasteJob,
  type TasteJobData,
} from "@/services/api/guestTasteApi";
import { useGuestTasteStream } from "@/hooks/useGuestTasteStream";

/**
 * P216 T0300 — THE RUN ITEM AT THE TOP OF THE SHELL
 *
 * A run started on the public doorway must not die at the login wall. The doorway saves the job
 * (sessionStorage `raisy_taste_job`, with the claim token that proves the run is HIS), and this item
 * carries it into the app shell: the same run, on every page, until its five emails are in his hands.
 *
 * FOUR THINGS IT DELIBERATELY IS NOT (each one is a rule someone would otherwise break here):
 *
 *  1. **NOT a second attention system.** It is a RUN item, never a badge: no red count, no unread dot,
 *     nothing to clear. P144's one store owns the counts and one number on one screen is the promise
 *     this product makes — this surface adds a status, not a number competing with it.
 *  2. **NOT a second poller when there is nothing to poll.** With no saved run it renders NOTHING and
 *     starts NO timer — the "silent when there is no run" half of T0301, which is why the saved job is
 *     read before any effect runs.
 *  3. **NOT a fixer of its own state.** `clearSavedTasteJob()` belongs to the CLAIM ("until the five are
 *     in the account, the run is still owed"), so an expired run is simply not rendered here — this
 *     item never deletes the evidence that he is owed something.
 *  4. **NOT a second cadence.** `POLL_EVERY_MS` is the doorway's own (GuestTasteWidget.tsx), so the two
 *     surfaces cannot disagree about how often a run is read.
 *
 * The progress words shown are the BACKEND'S OWN (`progress_label`), because the FE inventing a phase
 * from a percentage is how a progress bar starts lying. An unreachable engine keeps the last known
 * state rather than flashing a failure in a header the founder is using for something else.
 *
 * The vertical shell (`MobileHeader` — a 56px fixed bar holding the logo and the controls) is
 * deliberately NOT mounted in this pass: a third element in that bar is a layout decision, not a
 * detail. Named here so it is a decision rather than an oversight.
 */

/** The doorway's own cadence (GuestTasteWidget.tsx `POLL_EVERY_MS`) — reused, never reinvented. */
const POLL_EVERY_MS = 2000;

/** Where the doorway's payoff sends him, and where the five live once they are his. */
const PAYOFF_PATH = "/messages";

export function TasteRunItem() {
  const [saved] = useState(() => loadSavedTasteJob());
  const [job, setJob] = useState<TasteJobData | null>(null);

  /**
   * T0304's client half: the run's OWN events, pushed (D-029). It is used as the primary source and the
   * poll as the fallback, in that order — because a socket that never opens must not leave this item
   * frozen, and a poll that keeps running beside a live socket is the second cadence this component's
   * own rules forbid.
   */
  const stream = useGuestTasteStream(saved?.jobId ?? null, saved?.claimToken || null);

  useEffect(() => {
    if (!saved) return;
    // THE SOCKET REPLACES THIS TIMER THE MOMENT IT IS LIVE. Until it is, the poll is what keeps the
    // item honest — and once the stream has opened, asking the same question every 2 s alongside it is
    // exactly the second cadence this component refuses.
    if (stream.live) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = undefined;
    };

    const poll = async () => {
      const res = await fetchTaste(saved.jobId);
      if (cancelled) return;

      if (res.ok) {
        setJob(res.job);
        // The run has said everything it is going to say.
        if (res.job.state === "done" || res.job.state === "error") stop();
        return;
      }

      // `unknown_job` = the engine no longer holds this run. Nothing to show, nothing to say, and
      // nothing to delete: the saved claim is what proves he is owed the five.
      if (res.kind === "unknown_job") stop();
      // `network` / `server` keep the last known state — a hiccup is not a status change.
    };

    void poll();
    timer = setInterval(() => void poll(), POLL_EVERY_MS);

    return () => {
      cancelled = true;
      stop();
    };
  }, [saved, stream.live]);

  // No run in hand, or nothing honest to report yet: the header stays as it was.
  if (!saved || !job) return null;

  // The engine no longer holds it: silence, and the saved claim is untouched (it is what proves he is
  // owed the five).
  if (stream.gone) return null;

  // A run that stopped is the doorway's story to tell, not a header's: the hero already names it, and
  // a founder mid-task is not helped by the same news in the top bar.
  if (job.state === "error") return null;

  const total = job.draft_total ?? 5;
  const ready = Math.max(job.drafts?.length ?? job.drafted ?? 0, stream.events.filter((e) => e.kind === "draft").length);
  const isDone = job.state === "done" || stream.finalState === "done";

  if (isDone) {
    return (
      <Link
        to={PAYOFF_PATH}
        data-testid="taste-run-item"
        className="group flex items-center gap-2 min-w-0 rounded-md px-2 py-1 hover:bg-muted/50"
        title="The five emails your doorway wrote are in your messages"
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-full border border-green-500/30 bg-green-500/10 shrink-0">
          <Check className="h-3 w-3 text-green-500 stroke-[2.5]" />
        </span>
        <span className="text-sm font-semibold text-foreground leading-none truncate hidden sm:inline">
          Your 5 emails are ready
        </span>
        <span className="text-label-sm text-primary inline-flex items-center gap-1 shrink-0 group-hover:underline">
          Review these 5 Emails
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </Link>
    );
  }

  return (
    <div
      data-testid="taste-run-item"
      role="status"
      aria-live="polite"
      className="flex items-center gap-2 min-w-0 px-2"
      title={job.progress_label}
    >
      <Loader2 className="h-3.5 w-3.5 animate-spin text-subtle-foreground shrink-0" />
      <span className="text-sm text-subtle-foreground leading-none truncate max-w-[170px]">
        {job.progress_label}
      </span>
      {ready > 0 && (
        <span className="text-label-sm text-foreground tabular-nums leading-none shrink-0">
          {ready} of {total} ready
        </span>
      )}
    </div>
  );
}
